import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Stack,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  PointOfSale as CashierIcon,
  Visibility as ViewerIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  AccessTime as AccessTimeIcon,
  DeleteForever as DeleteIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import DeactivateBusinessDialog from './DeactivateBusinessDialog';
import DeleteBusinessDialog from './DeleteBusinessDialog';

const BusinessDetailsPage = () => {
  const theme = useTheme();
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchBusinessDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const fetchBusinessDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/super-admin/businesses/${businessId}`);
      setBusiness(response.data.business);
      setBranches(response.data.branches || []);
      setUsers(response.data.users || []);
      setRecentSales(response.data.recentSales || []);
      setActivity(response.data.activity || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching business details:', err);
      setError('Failed to load business details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (business.is_active) {
      // Open deactivate dialog instead of using prompt
      setDeactivateDialogOpen(true);
    } else {
      // Activate directly
      try {
        setActionLoading(true);
        await api.put(`/super-admin/businesses/${businessId}/toggle-status`, { reason: null });
        fetchBusinessDetails();
      } catch (err) {
        setError('Failed to activate business');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDeactivateConfirm = async (reason) => {
    try {
      setActionLoading(true);
      await api.put(`/super-admin/businesses/${businessId}/toggle-status`, { reason });
      setDeactivateDialogOpen(false);
      fetchBusinessDetails();
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to deactivate business');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBusiness = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/super-admin/businesses/${businessId}`);
      setDeleteDialogOpen(false);
      // Navigate back to super admin dashboard after successful deletion
      navigate('/super-admin');
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to delete business');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'business_owner':
        return <AdminIcon sx={{ fontSize: 18 }} />;
      case 'admin':
        return <AdminIcon sx={{ fontSize: 18 }} />;
      case 'cashier':
        return <CashierIcon sx={{ fontSize: 18 }} />;
      case 'viewer':
        return <ViewerIcon sx={{ fontSize: 18 }} />;
      default:
        return <PersonIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'business_owner':
        return 'primary';
      case 'admin':
        return 'secondary';
      case 'cashier':
        return 'success';
      case 'viewer':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[50], py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !business) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[50], py: 4 }}>
        <Container maxWidth="lg">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/super-admin')} sx={{ mb: 3 }}>
            Back to Dashboard
          </Button>
          <Alert severity="error">{error || 'Business not found'}</Alert>
        </Container>
      </Box>
    );
  }

  const totalRevenue = branches.reduce((sum, b) => sum + parseFloat(b.total_sales || 0), 0);
  const totalSales = branches.reduce((sum, b) => sum + parseInt(b.sales_count || 0), 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[50], py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/super-admin')}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            Back to Dashboard
          </Button>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              border: 1,
              borderColor: theme.palette.divider,
              borderRadius: 3
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: theme.palette.primary.main + '15',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {business.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={business.is_active ? 'Active' : 'Inactive'}
                      color={business.is_active ? 'success' : 'error'}
                      icon={business.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">{business.email}</Typography>
                  </Box>
                  {business.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">{business.phone}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {business.physical_address || 'No address'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Created {formatDate(business.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                  <Button
                    variant={business.is_active ? 'outlined' : 'contained'}
                    color={business.is_active ? 'error' : 'success'}
                    startIcon={business.is_active ? <CancelIcon /> : <CheckCircleIcon />}
                    onClick={handleToggleStatus}
                    disabled={actionLoading}
                    sx={{ textTransform: 'none' }}
                  >
                    {business.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {!business.is_active && (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={actionLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete Business
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <PeopleIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{users.length}</Typography>
                <Typography variant="body2" color="text.secondary">Users</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <StoreIcon sx={{ fontSize: 32, color: theme.palette.secondary.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{branches.length}</Typography>
                <Typography variant="body2" color="text.secondary">Branches</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <ReceiptIcon sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{totalSales}</Typography>
                <Typography variant="body2" color="text.secondary">Total Sales</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: theme.palette.info.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>KES {totalRevenue.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper elevation={0} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label={`Users (${users.length})`} sx={{ textTransform: 'none' }} />
            <Tab label={`Branches (${branches.length})`} sx={{ textTransform: 'none' }} />
            <Tab label={`Recent Sales (${recentSales.length})`} sx={{ textTransform: 'none' }} />
            <Tab label="Activity Log" sx={{ textTransform: 'none' }} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Users Tab */}
            {activeTab === 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Branches</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No users found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: theme.palette.primary.main,
                                  mr: 1.5,
                                  fontSize: 14
                                }}
                              >
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {user.first_name} {user.last_name}
                                  {user.is_business_owner && (
                                    <Chip
                                      label="Owner"
                                      size="small"
                                      color="primary"
                                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{user.username}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getRoleIcon(user.role)}
                              label={user.role?.replace('_', ' ')}
                              size="small"
                              color={getRoleColor(user.role)}
                              sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.status}
                              size="small"
                              color={user.status === 'active' ? 'success' : 'error'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.assigned_branches || 0}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={user.last_login ? formatDateTime(user.last_login) : 'Never logged in'}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                                <Typography
                                  variant="body2"
                                  color={user.last_login ? 'text.primary' : 'text.secondary'}
                                >
                                  {getTimeAgo(user.last_login)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(user.created_at)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Branches Tab */}
            {activeTab === 1 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sales Count</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {branches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No branches found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      branches.map((branch) => (
                        <TableRow key={branch.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <StoreIcon sx={{ color: theme.palette.secondary.main, mr: 1.5 }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {branch.name}
                                  {branch.is_main_branch && (
                                    <Chip
                                      label="Main"
                                      size="small"
                                      color="primary"
                                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Typography>
                                {branch.address && (
                                  <Typography variant="caption" color="text.secondary">
                                    {branch.address}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {branch.branch_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={branch.status}
                              size="small"
                              color={branch.status === 'active' ? 'success' : 'error'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{branch.sales_count || 0}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              KES {parseFloat(branch.total_sales || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(branch.created_at)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Recent Sales Tab */}
            {activeTab === 2 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Sale #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cashier</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentSales.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No sales found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentSales.map((sale) => (
                        <TableRow key={sale.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {sale.sale_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{sale.branch_name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {sale.first_name} {sale.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                              KES {parseFloat(sale.total_amount).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sale.payment_method}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDateTime(sale.created_at)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Activity Log Tab */}
            {activeTab === 3 && (
              <Box>
                {activity.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No activity logged</Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {activity.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: theme.palette.grey[50],
                          borderRadius: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <HistoryIcon sx={{ color: 'text.secondary', mr: 2, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.action_type?.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.first_name} {item.last_name} - {formatDateTime(item.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Deactivate Business Dialog */}
        <DeactivateBusinessDialog
          open={deactivateDialogOpen}
          onClose={() => setDeactivateDialogOpen(false)}
          onConfirm={handleDeactivateConfirm}
          businessName={business?.name || ''}
          loading={actionLoading}
        />

        {/* Delete Business Dialog */}
        <DeleteBusinessDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteBusiness}
          businessName={business?.name || ''}
          loading={actionLoading}
        />
      </Container>
    </Box>
  );
};

export default BusinessDetailsPage;
